import React from "react";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { useAuth } from "../../context/AuthContext";
import { normalizeProjectCapabilities } from "../../utils/projectCapabilities";
import { normalizeProjectModules } from "../../utils/projectModules";
import { billingAccessLabel, getProjectBillingAccess } from "../../utils/projectBillingAccess";
import { resolveProjectNavigation } from "../../utils/dashboardNavigation";
import { isSuperAdmin } from "../../utils/roles";
import MobileProjectSettingsLayout from "./mobile/MobileProjectSettingsLayout";
import DashboardNavigationIcon from "./DashboardNavigationIcon";
import { PROJECT_SETTINGS_SECTIONS, getProjectSettingsSection } from "./projectSettingsSections";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const SETTINGS_SECTIONS = PROJECT_SETTINGS_SECTIONS
  .filter((section) => section.hideFromSettingsNavigation !== true)
  .map((section) => ({
    ...section,
    icon: <DashboardNavigationIcon name={section.iconKey} />,
  }));

function currentSection(pathname) {
  return getProjectSettingsSection(pathname);
}

function areaStatus(label, tone = "default") {
  return { label, tone };
}

export default function ProjectSettingsLayout() {
  const { projectId } = useParams();
  const mobile = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [readiness, setReadiness] = React.useState(null);

  const project = React.useMemo(() => {
    const matched = projects.find(
      (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
    );
    const activeMatches =
      activeProject?.slug === projectId ||
      activeProject?.id === projectId ||
      activeProject?._id === projectId;
    return matched || (activeMatches ? activeProject : null);
  }, [projects, projectId, activeProject]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    if (project && project.slug !== activeProject?.slug) setActiveProject(project);
  }, [project, activeProject?.slug, setActiveProject]);

  React.useEffect(() => {
    let cancelled = false;
    const fallback = project?.readiness || null;
    setReadiness(fallback);

    if (!projectId) return () => { cancelled = true; };

    projectsApi
      .getReadiness(projectId)
      .then((data) => {
        if (!cancelled) setReadiness(data?.readiness || fallback);
      })
      .catch(() => {
        if (!cancelled) setReadiness(fallback);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, project?.readiness?.computedAt]);

  const modules = normalizeProjectModules(project?.modules);
  const capabilities = normalizeProjectCapabilities(project?.capabilities);
  const activeModules = Object.values(modules).filter(Boolean).length;
  const activeCapabilities = Object.values(capabilities).filter(Boolean).length;
  const billingAccess = getProjectBillingAccess(project);
  const billingChipColor = billingAccess.isFull
    ? "success"
    : billingAccess.isGrace
      ? "warning"
      : "error";
  const subscriptionLabel = billingAccessLabel(billingAccess);
  const subscriptionNeedsAttention = billingAccess.needsPaymentAction || billingAccess.isSuspended;
  const subscriptionIsTrial = billingAccess.isTrial;
  const section = currentSection(location.pathname);
  const settingsNavigation = React.useMemo(
    () => resolveProjectNavigation({
      project,
      memberAccess: project?.memberAccess,
      superAdmin: isSuperAdmin(user),
      pathname: location.pathname,
    }).toolsByKey.settings,
    [project, user, location.pathname],
  );
  const settingsAccessByValue = React.useMemo(
    () => Object.fromEntries(
      (settingsNavigation?.secondaryTools || []).map((tool) => [
        String(tool.key || "").replace(/^settings-/, ""),
        tool,
      ]),
    ),
    [settingsNavigation],
  );
  const visibleSettingsSections = React.useMemo(
    () => !project
      ? SETTINGS_SECTIONS
      : SETTINGS_SECTIONS.filter((item) => settingsAccessByValue[item.value]?.visible !== false),
    [project, settingsAccessByValue],
  );

  const readinessFacts = readiness?.facts || {};
  const identityReady = readinessFacts.identity
    ? readinessFacts.identity.complete === true
    : Boolean(project?.name && project?.slug);
  const businessDataReady = identityReady && Boolean(project?.businessProfile);

  const guidedAreas = React.useMemo(() => [
      {
        key: "business",
        title: "Datos del negocio",
        description: businessDataReady
          ? "Identidad y tipo de negocio definidos."
          : "Completá la identidad y el tipo de negocio para orientar el panel.",
        status: businessDataReady ? areaStatus("Listo", "success") : areaStatus("Revisar", "warning"),
        icon: <SettingsRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/general?area=business`,
      },
      {
        key: "subscription",
        title: "Plan y suscripción",
        description: subscriptionLabel,
        status: subscriptionNeedsAttention
          ? areaStatus("Revisar", "warning")
          : subscriptionIsTrial
            ? areaStatus("Prueba")
            : areaStatus("Activo", "success"),
        icon: <WorkspacePremiumRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/suscripcion`,
      },
      {
        key: "permissions",
        title: "Permisos y equipo",
        description: "Revisá quién entra al proyecto, sus roles, cajas y permisos.",
        status: areaStatus("Mantenimiento"),
        icon: <ManageAccountsRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/accesos`,
      },
      {
        key: "capabilities",
        title: "Capacidades",
        description: activeCapabilities
          ? `${activeCapabilities} capacidades adaptan el panel a tu operación.`
          : "Elegí solamente las capacidades que realmente usa el negocio.",
        status: activeCapabilities ? areaStatus("Configuradas", "success") : areaStatus("Personalizar"),
        icon: <TuneRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/general?area=capabilities`,
      },
      {
        key: "advanced",
        title: "Configuración avanzada",
        description: "Mantenimiento técnico y opciones que no forman parte de la operación diaria.",
        status: areaStatus("Opcional"),
        icon: <SettingsSuggestRoundedIcon fontSize="small" />,
        path: `/dashboard/proyectos/${projectId}/configuracion/general?area=advanced`,
      },
    ], [
    activeCapabilities,
    businessDataReady,
    projectId,
    subscriptionIsTrial,
    subscriptionLabel,
    subscriptionNeedsAttention,
  ]);

  const visibleGuidedAreas = React.useMemo(() => {
    if (!project) return guidedAreas;
    const sectionForArea = {
      subscription: "suscripcion",
      permissions: "accesos",
    };
    return guidedAreas.filter((area) => {
      const targetSection = sectionForArea[area.key] || "general";
      return settingsAccessByValue[targetSection]?.visible !== false;
    });
  }, [project, guidedAreas, settingsAccessByValue]);

  const handleSectionChange = (_event, value) => {
    navigate(`/dashboard/proyectos/${projectId}/configuracion/${value}`);
  };

  const openArea = (area) => navigate(area.path);

  // Cobros online y Notificaciones conservan sus rutas legacy dentro de
  // /configuracion, pero conceptualmente pertenecen a Ventas y Más. Evitamos
  // envolverlas con el encabezado, tarjetas y pestañas de Configuración para que
  // la navegación nueva sea la que marque su contexto real.
  if (["ecommerce", "notificaciones"].includes(section)) {
    return <Outlet />;
  }

  if (mobile) {
    return (
      <MobileProjectSettingsLayout
        sections={visibleSettingsSections}
        section={section}
        onSectionChange={(value) => handleSectionChange(null, value)}
        project={project}
        activeModules={activeModules}
        billingLabel={billingAccessLabel(billingAccess)}
        billingColor={billingChipColor}
        guidedAreas={visibleGuidedAreas}
        onAreaOpen={openArea}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Configuración
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
            {project?.name || projectId} · cerrá lo esencial del proyecto y volvé acá para mantenimiento
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            icon={<WorkspacePremiumRoundedIcon />}
            label={`${activeModules} módulos activos`}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
          <Chip
            label={billingAccessLabel(billingAccess)}
            color={billingChipColor}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
        </Stack>
      </Stack>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                Cierre y mantenimiento del proyecto
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                Revisá primero lo que merece atención. Las tareas operativas siguen en sus secciones habituales del panel.
              </Typography>
            </Box>
            {readiness?.score != null ? (
              <Chip
                icon={<CheckCircleRoundedIcon />}
                label={`${readiness.score}% del inicio listo`}
                color={readiness.score >= 100 ? "success" : "default"}
                variant="outlined"
                sx={{ alignSelf: "flex-start", fontWeight: 900 }}
              />
            ) : null}
          </Stack>

          <Grid container spacing={1.25}>
            {visibleGuidedAreas.map((area) => (
              <Grid item xs={12} sm={6} lg={4} key={area.key}>
                <ButtonBase
                  onClick={() => openArea(area)}
                  aria-label={`Abrir ${area.title}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    alignItems: "stretch",
                    textAlign: "left",
                    borderRadius: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      p: 1.55,
                      borderRadius: 3,
                      border: "1px solid rgba(25,36,47,.08)",
                      bgcolor: area.status.tone === "warning" ? "rgba(237,108,2,.035)" : "#fff",
                    }}
                  >
                    <Stack direction="row" spacing={1.15} alignItems="flex-start">
                      <Box sx={{ mt: .15, color: area.status.tone === "warning" ? "warning.main" : BRAND.orange }}>
                        {area.icon}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                            {area.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={area.status.label}
                            color={area.status.tone === "success" ? "success" : area.status.tone === "warning" ? "warning" : "default"}
                            variant={area.status.tone === "default" ? "outlined" : "filled"}
                            sx={{ height: 22, fontSize: 10.5, fontWeight: 900 }}
                          />
                        </Stack>
                        <Typography variant="caption" sx={{ display: "block", mt: .45, color: "rgba(25,36,47,.58)", lineHeight: 1.35 }}>
                          {area.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </ButtonBase>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card
        elevation={0}
        sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", overflow: "hidden" }}
      >
        <Box sx={{ borderBottom: "1px solid rgba(25,36,47,0.08)", bgcolor: "#fff" }}>
          <Tabs
            value={section}
            onChange={handleSectionChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Secciones de configuración del proyecto"
            sx={{
              px: { xs: 1, md: 2 },
              minHeight: 62,
              "& .MuiTab-root": {
                minHeight: 62,
                textTransform: "none",
                fontWeight: 900,
                px: 2,
              },
              "& .Mui-selected": { color: `${BRAND.orange} !important` },
              "& .MuiTabs-indicator": { bgcolor: BRAND.orange, height: 3 },
            }}
          >
            {visibleSettingsSections.map((item) => (
              <Tab
                key={item.value}
                value={item.value}
                label={item.label}
                icon={item.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>
        <CardContent sx={{ p: { xs: 2, md: 3.5 }, bgcolor: "#FBFBFD" }}>
          <Outlet />
        </CardContent>
      </Card>
    </Stack>
  );
}
